import {ApplicationRef, Component, HostListener, Input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {Store} from '@ngxs/store';
import {
  Container,
  Drawer,
  DrawImage,
  DrawLine,
  DrawPolyline,
  DrawTextBox,
  DrawTool,
  EditNodeTool,
  EditTableTool,
  ElementType,
  ForeignObjectView,
  FreeDrawer, GroupView,
  HighlightTool,
  ImageView, LineView, Path, PathView,
  PointerTool,
  SelectTool,
  Style,
  SVGEvent,
  TableView,
  TextBoxView,
  Tool
} from '@app/modules/svg/public-api';
import {Whiteboard, WhiteboardAction, WhiteboardState} from '@app/state/whiteboard.state';
import {DrawRectangle} from '@app/modules/svg/service/tool/draw/element/shape/pointed/polygon/rectangle/DrawRectangle';
import {DrawEllipse} from '@app/modules/svg/service/tool/draw/element/shape/circular/DrawEllipse';
import {DrawCircle} from '@app/modules/svg/service/tool/draw/element/shape/circular/DrawCircle';
import {
  DrawIsoscelesTriangle
} from '@app/modules/svg/service/tool/draw/element/shape/pointed/polygon/triangle/DrawIsoscelesTriangle';
import {
  DrawRightTriangle
} from '@app/modules/svg/service/tool/draw/element/shape/pointed/polygon/triangle/DrawRightTriangle';
import {DrawTable} from '@app/modules/svg/service/tool/draw/element/complex/DrawTable';
import {DrawPolygon} from '@app/modules/svg/service/tool/draw/element/shape/pointed/polygon/DrawPolygon';
import {Tools} from '@app/modules/svg/dataSource/Tools';
import {AccessType, JsonLogAction, Key, KeyCode, ViewportTab} from '@app/modules/shared/data/constants';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {
  CustomColorPickerComponent
} from '@app/modules/shared/components/dialogs/custom-color-picker/custom-color-picker.component';
import {AssignmentAction, AssignmentState, AssignmentStateModel} from '@app/state/assignment.state';
import {AssignmentService} from '@app/modules/whiteboard/services/assignment.service';
import {CurriculumService} from '@app/services/curriculum.service';
import {JsonLogService} from '@app/modules/shared/services/json-log.service';
import {EraserTool} from '@app/modules/svg/service/tool/eraser/EraserTool';
import {DrawCoordinatePlane2} from '@app/modules/svg/service/tool/draw/element/complex/cartesian2/DrawCoordinatePlane2';
import {DrawNumberLine2} from '@app/modules/svg/service/tool/draw/element/complex/cartesian2/DrawNumberLine2';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';
import {ElementView} from '@app/modules/svg/element/ElementView';
import {WebSocket} from '@app/modules/shared/services/web-socket.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {ErrorDialogComponent} from '@app/modules/shared/components/dialogs/error-dialog/error-dialog.component';
import {GeoGebraService} from '@app/modules/whiteboard/services/geo-gebra.service';

import {CoreService} from '@app/services/core.service';
import {WebPortalMessagingService} from '@app/services/web-portal-messaging.service';
import {NumberLineView2} from '@app/modules/svg/element/complex/cartesian2/NumberLineView2';
import {CoordinatePlaneView2} from '@app/modules/svg/element/complex/cartesian2/CoordinatePlaneView2';
import {
  DrawParallelogram
} from '@app/modules/svg/service/tool/draw/element/shape/pointed/polygon/parallelogram/DrawParallelogram';
import {
  DrawIsoscelesTrapezoid
} from '@app/modules/svg/service/tool/draw/element/shape/pointed/polygon/trapezoid/DrawIsoscelesTrapezoid';
import {DrawCube} from '@app/modules/svg/service/tool/draw/element/shape/pointed/projected-3d/DrawCube';
import {DrawPyramid} from '@app/modules/svg/service/tool/draw/element/shape/pointed/projected-3d/DrawPyramid';
import {DrawPrism} from '@app/modules/svg/service/tool/draw/element/shape/pointed/projected-3d/DrawPrism';
import {DrawCylinder} from '@app/modules/svg/service/tool/draw/element/shape/pointed/projected-3d/DrawCylinder';
import {DrawCone} from '@app/modules/svg/service/tool/draw/element/shape/pointed/projected-3d/DrawCone';
import {DrawSphere} from '@app/modules/svg/service/tool/draw/element/shape/pointed/projected-3d/DrawSphere';
import {DrawParabola} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/graphic/DrawParabola';
import {
  DrawAbsoluteValue
} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/graphic/DrawAbsoluteValue';
import {FillTool} from '@app/modules/svg/service/tool/fill/FillTool';
import {DrawSquareRoot} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/graphic/DrawSquareRoot';
import {DrawCos} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/graphic/DrawCos';
import {DrawSin} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/graphic/DrawSin';
import {DrawTan} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/graphic/DrawTan';
import {DrawSegment} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/graphic/DrawSegment';
import {PointType} from '@app/modules/svg/element/complex/cartesian/graphic/SegmentView';

@Component({
  selector: 'app-staff-toolbar',
  templateUrl: './staff-toolbar.component.html',
  styleUrls: ['./staff-toolbar.component.css']
})
export class StaffToolbarComponent implements OnInit {
  @Input()
  public whiteboard: Whiteboard;
  @Input()
  public enableGlobalListeners: boolean;
  @Input()
  public isInRestrictedMode: boolean;
  public localUser: LocalUser;
  public classroom: ClassroomStateModel;
  public assignment: AssignmentStateModel;
  public selectedOption: string;

  protected readonly WhiteboardService = WhiteboardService;

  @Input()
  public whiteboardService: WhiteboardService;

  @ViewChildren('colorPicker')
  public colorPickers: QueryList<CustomColorPickerComponent>;

  public activeDrawer: { linear: Drawer; shape: Drawer; cartesian: Drawer } = { linear: undefined, shape: undefined, cartesian: undefined };
  public lastActive: { tool: Tool; drawer: Drawer } = { tool: undefined, drawer: undefined };

  public colorsList = ['#ffffff', '#fdba13', '#ec1f24', '#9a4598', '#000000', '#2d67b1', '#0daab4', '#00964b'];
  public strokesList = [1, 2, 3, 4];
  public strokesMoreList = [6, 13, 20];
  public dashStylesList = ['0', '8 4', '16 4', '16 4 0 4', '16 4 0 4 0 4'];
  public fontSizeList = [12, 14, 16, 20];
  public fontSizeMoreList = [24, 28, 36, 48, 64, 90];
  public gridSideList = [10, 15, 20, 25, 30, 35, 40];
  public pyramidVerticesList = [3, 4, 5, 6, 7, 8, 9, 10];
  public prismVerticesList = [3, 4, 5, 6, 7, 8, 9, 10];
  public whiteboardHeightPercent: number = 0;

  public selectedPyramidVertices: number = this.pyramidVerticesList[0];
  public selectedPrismVertices: number = this.prismVerticesList[2];

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public ViewportTab = ViewportTab;

  public isDialogOpen = {
    fillColor: false,
    strokeColor: false,
    dashStyles: false,
    fontSizeMore: false,
    strokesMore: false,
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public KeyCode = KeyCode;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public AccessType = AccessType;

  private turnOnDefaultTool: boolean;

  constructor(
    private coreService: CoreService,
    private jsonLogService: JsonLogService,
    private assignmentService: AssignmentService,
    private curriculumService: CurriculumService,
    private webPortalMessagingService: WebPortalMessagingService,
    private applicationRef: ApplicationRef,
    private dialogService: NgbModal,
    private wrtcProviderService: WRTCProviderService,
    private geoGebraService: GeoGebraService,
    private store: Store
  ) {}

  public ngOnInit(): void {
    this.listenStoreChanges();
    if (this.localUser.accessType === AccessType.SUPPORT) {
      this.isInRestrictedMode = true;
      this.whiteboardService.isInRestrictedMode = this.isInRestrictedMode;
    }

    this.init();
    this.whiteboardService.onMessageSendError
      .subscribe(({status, messages}: {status: WebSocket.MessageSendStatus; messages: {[key: string]: string}}): void => {
        this.showErrorDialog(status, messages);
      });
    this.whiteboardService.extendEventEmitter.subscribe((height: number) => {
      this.whiteboardHeightPercent = Math.round(height / WhiteboardState.MAX_HEIGHT * 100);
    });
  }

  @HostListener('click', ['$event'])
  public onClickGlobalListener(event: MouseEvent): void {
    if (!this.enableGlobalListeners) {
      return;
    }

    this.isDialogOpen.fontSizeMore = false;
    this.isDialogOpen.dashStyles = false;
    this.isDialogOpen.strokesMore = false;
    this.colorPickers.forEach((cp: CustomColorPickerComponent) => {
      if (!(event.target as Element).closest('.picker-dialog, .color-item-more')) {
        cp.closeColorPicker();
      }
    });
  }

  /** complete a click-drawn element (polygon, polyline...) on any toolbar interaction */
  @HostListener('mousedown')
  public onMouseDownGlobalListener(): void {
    const drawTool: DrawTool | undefined = this.wbTools?.drawTool;
    if (!drawTool?.isDrawing) {
      return;
    }

    drawTool.stopDrawing();
    this.wbContainer.blur();
  }

  @HostListener('window:keydown', ['$event'])
  public keyDown(event: KeyboardEvent): void {
    if (!this.enableGlobalListeners) {
      return;
    }

    if (event.ctrlKey && event.code === KeyCode.KEY_C) {
      this.copyFocused();
    }
    if (event.ctrlKey && event.code === KeyCode.KEY_X) {
      this.cutFocused();
    }
    if (event.ctrlKey && event.code === KeyCode.KEY_V) {
      this.paste();
    }
    if (event.ctrlKey && event.code === KeyCode.KEY_A) {
      if (!(this.wbTools.drawTool.isOn() && this.wbTools.drawTool.getDrawer() instanceof DrawTextBox)) {
        this.selectAll();
        event.preventDefault(); /* remove browser all selection */
      }
    }
    if (event.altKey && event.code === KeyCode.KEY_S) {
      if (this.whiteboard.sharedUsers) {
        this.unshare();
      } else {
        this.share();
      }
    }

    /* UNDO/REDO KEYBINDINGS */
    if (event.ctrlKey && event.code === 'KeyZ') {
        event.preventDefault();
    }
    if (event.ctrlKey && event.code === 'KeyY') {
        event.preventDefault();
    }

    if (event.ctrlKey && event.code === 'Space') {
      let lastActiveDrawer: Drawer;
      if (this.wbActiveTool instanceof DrawTool) {
        lastActiveDrawer = this.wbActiveTool.getDrawer();
      }
      const lastActiveTool = this.wbActiveTool;

      if (this.lastActive.tool instanceof DrawTool) {
        this.turnOnDefaultTool = false;
        this.lastActive.tool.setDrawer(this.lastActive.drawer);
        this.turnOnDefaultTool = true;
      }
      this.lastActive.tool.on();

      this.lastActive.drawer = lastActiveDrawer;
      this.lastActive.tool = lastActiveTool;

      event.preventDefault();
    }

    if (event.shiftKey && event.key === 'PageUp') {
      this.orderTop();
      event.preventDefault(); /* disable window scrolling */
    }
    if (event.shiftKey && event.key === 'PageDown') {
      this.orderBottom();
      event.preventDefault(); /* disable window scrolling */
    }
    if (event.code === KeyCode.ARR_LEFT && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: -5, y: 0});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }
    if (event.code === KeyCode.ARR_RIGHT && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: +5, y: 0});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }
    if (event.code === KeyCode.ARR_DOWN && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: 0, y: +5});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }
    if (event.code === KeyCode.ARR_UP && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: 0, y: -5});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }

    if (event.shiftKey) {
      this.wbContainer.perfect = true;
    }
    if (event.code === KeyCode.ESC) {
      if (this.wbTools.drawTool.isOn()) {
        this.wbTools.drawTool.stopDrawing();
      } else if (this.wbTools.selectTool.isOn()) {
        this.wbContainer.blur();
      }
    }
    if (event.ctrlKey) {
      this.wbContainer.multiSelect();
      this.wbContainer.multiSelect();
    }
  }

  @HostListener('window:keyup', ['$event'])
  public keyUp(event: KeyboardEvent): void {
    if (!this.enableGlobalListeners) {
      return;
    }

    if (event.ctrlKey && event.code === 'KeyZ') {
      this.undo();
    }
    if (event.ctrlKey && event.code === 'KeyY') {
      this.redo();
    }
    if (event.key === Key.SHIFT) {
      this.wbContainer.perfect = false;
    }
    if (event.code === KeyCode.DELETE) {
      const active = document.activeElement;
      if (active instanceof HTMLInputElement && (active.type === 'text' || active.type === 'number')) {return;}
      this.deleteSelected();
    }
    if (event.key === Key.CTRL) {
      this.wbContainer.singleSelect();
    }
  }

  public get isDrawing(): boolean {
    /* not applicable case
    * this.wbActiveTool instanceof DrawTool && this.wbActiveTool.isDrawing;
    * */
    return !(this.wbActiveTool instanceof DrawTool) || !this.wbActiveTool.isDrawing;
  }
  public get isGroupApplicable(): boolean {
    return this.wbContainer?.focused.canGroup;
  }
  public get isUngroupApplicable(): boolean {
    return this.wbContainer?.focused.canUngroup;
  }
  public get isSelectToolOn(): boolean {
    return this.wbActiveTool instanceof SelectTool;
  }
  public get isEraserToolOn(): boolean {
    return this.wbActiveTool instanceof EraserTool;
  }
  public get isEditNodeToolOn(): boolean {
    return this.wbActiveTool instanceof EditNodeTool;
  }
  public get isEditTableToolOn(): boolean {
    return this.wbActiveTool instanceof EditTableTool;
  }
  public get isGridOn(): boolean {
    return this.wbContainer?.grid?.isGridOn();
  }
  public get isSnapOn(): boolean {
    return this.wbContainer?.grid?.isSnapOn();
  }
  public get isRectangleToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawRectangle;
  }
  public get isParallelogramToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawParallelogram;
  }
  public get isIsoscelesTrapezoidToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawIsoscelesTrapezoid;
  }
  public get isEllipseToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawEllipse;
  }
  public get isCircleToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawCircle;
  }
  public get isIsoscelesTriangleToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawIsoscelesTriangle;
  }
  public get isRightTriangleToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawRightTriangle;
  }
  public get isUnitCircleChartToolOn(): boolean {
      return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawImage;
  }
  public get isTableToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawTable;
  }
  public get isLineToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawLine;
  }
  public get isPolylineToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawPolyline;
  }
  public get isPolygonToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawPolygon;
  }
  public get isFreeToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof FreeDrawer;
  }
  public get isTextBoxToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawTextBox;
  }
  public get isCoordinatePlaneToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawCoordinatePlane2;
  }
  public get isNumberLineToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawNumberLine2;
  }
  public get isParabolaToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawParabola;
  }
  public get isAbsoluteValueToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawAbsoluteValue;
  }
  public get isSquareRootToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawSquareRoot;
  }
  public get isSinToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawSin;
  }
  public get isCosToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawCos;
  }
  public get isTanToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawTan;
  }
  public get isSegmentToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawSegment && (this.wbActiveTool.getDrawer() as DrawSegment).endPointType !== PointType.ARROW;
  }
  public get isRayToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawSegment && (this.wbActiveTool.getDrawer() as DrawSegment).endPointType === PointType.ARROW;
  }
  public get isCubeToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawCube;
  }
  public get isPyramidToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawPyramid;
  }
  public get isPrismToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawPrism;
  }
  public get isCylinderToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawCylinder;
  }
  public get isConeToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawCone;
  }
  public get isSphereToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawSphere;
  }
  public get isHighlightToolOn(): boolean {
    return this.wbActiveTool instanceof HighlightTool;
  }
  public get isPointerToolOn(): boolean {
    return this.wbActiveTool instanceof PointerTool;
  }
  public get isFillToolOn(): boolean {
    return this.wbActiveTool instanceof FillTool;
  }

  public get wbContainer(): Container | undefined {
    return this.whiteboard?.container;
  }
  public get wbFillToolColor(): string {
    return this.wbTools.fillTool.fillColor;
  }
  public get wbStyle(): Style | undefined {
    return this.whiteboard?.container?.style;
  }
  public get wbTools(): Tools | undefined {
    return this.whiteboard?.container?.tools;
  }
  public get wbActiveTool(): Tool | undefined {
    return this.whiteboard?.container?.tools?.activeTool;
  }

  public undo(): void {
    if (!this.whiteboard.sharedUsers) {
      this.whiteboardService.sendUndo();
    }
  }
  public redo(): void {
    if (!this.whiteboard.sharedUsers) {
      this.whiteboardService.sendRedo();
    }
  }
  public grid(): void {
    if (this.wbContainer?.grid.isGridOn()) {
      this.wbContainer.grid.gridOff();
      this.wbContainer.grid.snapOff();
      this.whiteboardService.sendGridOff();
      this.whiteboardService.sendSnapOff();
    } else {
      this.wbContainer?.grid.gridOn();
      this.whiteboardService.sendGridOn();
    }
  }
  public snap(): void {
    if (this.wbContainer?.grid.isSnapOn()) {
      this.wbContainer.grid.snapOff();
      this.whiteboardService.sendSnapOff();
    } else {
      if (!this.wbContainer?.grid.isGridOn()) {
        this.wbContainer.grid.gridOn();
        this.whiteboardService.sendGridOn();
      }
      this.wbContainer?.grid.snapOn();
      this.whiteboardService.sendSnapOn();
    }
  }
  public group(): void {
    this.wbContainer?.focused.group();
  }
  public ungroup(): void {
    this.wbContainer?.focused.ungroup();
  }
  public toPath(): void {
    this.wbContainer?.focused.toPath();
  }
  public select(): void {
    this.activateTool('Select', () => this.wbTools.selectTool.on());
  }
  public eraser(): void {
    this.activateTool('Eraser', () => this.wbTools.eraserTool.on());
  }
  public unitCircleChart(): void {
    this.activateTool('Unit Circle Chart', () => {
      this.activeDrawer.cartesian = this.wbContainer.drawers.image;
      this.wbContainer.drawers.image.src = 'assets/img/whiteboard/unit-circle-chart.svg';
      this.wbContainer.drawers.image.proportionalResizable = true;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.image);
      this.wbTools.drawTool.on();
    });
  }
  public rectangle(): void {
    this.activateTool('Rectangle', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.rectangle;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.rectangle);
      this.wbTools.drawTool.on();
    });
  }
  public parallelogram(): void {
    this.activateTool('Parallelogram', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.parallelogram;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.parallelogram);
      this.wbTools.drawTool.on();
    });
  }
  public isoscelesTrapezoid(): void {
    this.activateTool('Isosceles Trapezoid', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.isoscelesTrapezoid;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.isoscelesTrapezoid);
      this.wbTools.drawTool.on();
    });
  }
  public ellipse(): void {
    this.activateTool('Ellipse', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.ellipse;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.ellipse);
      this.wbTools.drawTool.on();
    });
  }
  public circle(): void {
    this.activateTool('Circle', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.circle;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.circle);
      this.wbTools.drawTool.on();
    });
  }
  public triangle(): void {
    this.activateTool('Triangle', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.isoscelesTriangle;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.isoscelesTriangle);
      this.wbTools.drawTool.on();
    });
  }
  public rightTriangle(): void {
    this.activateTool('Right Triangle', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.rightTriangle;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.rightTriangle);
      this.wbTools.drawTool.on();
    });
  }
  public table(): void {
    this.activateTool('Table', () => {
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.table);
      this.wbTools.drawTool.on();
    });
  }
  public line(): void {
    this.activateTool('Line', () => {
      this.activeDrawer.linear = this.wbContainer.drawers.line;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.line);
      this.wbTools.drawTool.on();
    });
  }
  public polyline(): void {
    this.activateTool('Polyline', () => {
      this.activeDrawer.linear = this.wbContainer.drawers.polyline;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.polyline);
      this.wbTools.drawTool.on();
    });
  }
  public polygon(): void {
    this.activateTool('Polygon', () => {
      this.activeDrawer.linear = this.wbContainer.drawers.polygon;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.polygon);
      this.wbTools.drawTool.on();
    });
  }
  public free(): void {
    this.activateTool('Pen', () => {
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.free);
      this.wbTools.drawTool.on();
    });
  }
  public highlighter(): void {
    this.activateTool('Highlighter', () => this.wbTools?.highlightTool.on());
  }
  public pointer(): void {
    this.activateTool('Pointer', () => this.wbTools.pointerTool.on());
  }
  public textBox(): void {
    this.activateTool('Text Box', () => {
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.textBox);
      this.wbTools.drawTool.on();
    });
  }

  public coordinatePlane2(): void {
    this.activateTool('CP', () => {
      this.activeDrawer.cartesian = this.wbContainer.drawers.coordinatePlane2;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.coordinatePlane2);
      this.wbTools.drawTool.on();
    });
  }
  public numberLine2(): void {
    this.activateTool('NL', () => {
      this.activeDrawer.cartesian = this.wbContainer.drawers.numberLine2;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.numberLine2);
      this.wbTools.drawTool.on();
    });
  }

  public cube(): void {
    this.activateTool('Cube', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.cube;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.cube);
      this.wbTools.drawTool.on();
    });
  }

  public pyramid(): void {
    this.activateTool('Pyramid', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.pyramid;
      this.wbContainer.drawers.pyramid.baseVertexCount = this.selectedPyramidVertices;

      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.pyramid);
      this.wbTools.drawTool.on();
    });
  }

  public prism(): void {
    this.activateTool('Right Prism', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.prism;
      this.wbContainer.drawers.prism.baseVertexCount = this.selectedPrismVertices;

      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.prism);
      this.wbTools.drawTool.on();
    });
  }

  public cylinder(): void {
    this.activateTool('Cylinder', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.cylinder;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.cylinder);
      this.wbTools.drawTool.on();
    });
  }

  public cone(): void {
    this.activateTool('Cone', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.cone;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.cone);
      this.wbTools.drawTool.on();
    });
  }

  public sphere(): void {
    this.activateTool('Sphere', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.sphere;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.sphere);
      this.wbTools.drawTool.on();
    });
  }

  public parabola(): void {
    this.activateTool('Parabola', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.parabola;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.parabola);
      this.wbTools.drawTool.on();
    });
  }
  public absoluteValue(): void {
    this.activateTool('Absolute Value', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.absoluteValue;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.absoluteValue);
      this.wbTools.drawTool.on();
    });
  }
  public squareRoot(): void {
    this.activateTool('Square Root', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.squareRoot;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.squareRoot);
      this.wbTools.drawTool.on();
    });
  }
  public sin(): void {
    this.activateTool('Sin', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.sin;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.sin);
      this.wbTools.drawTool.on();
    });
  }
  public cos(): void {
    this.activateTool('Cos', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.cos;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.cos);
      this.wbTools.drawTool.on();
    });
  }
  public tan(): void {
    this.activateTool('Tan', () => {
      this.activeDrawer.shape = this.wbContainer.drawers.tan;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.tan);
      this.wbTools.drawTool.on();
    });
  }
  public segment(): void {
    this.activateTool('Segment', () => {
      this.wbContainer.drawers.segment.startPointType = PointType.OPEN;
      this.wbContainer.drawers.segment.endPointType = PointType.OPEN;

      this.activeDrawer.shape = this.wbContainer.drawers.segment;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.segment);
      this.wbTools.drawTool.on();
    });
  }
  public ray(): void {
    this.activateTool('Ray', () => {
      this.wbContainer.drawers.segment.startPointType = PointType.OPEN;
      this.wbContainer.drawers.segment.endPointType = PointType.ARROW;

      this.activeDrawer.shape = this.wbContainer.drawers.segment;
      this.wbTools.drawTool.setDrawer(this.wbContainer.drawers.segment);
      this.wbTools.drawTool.on();
    });
  }

  public copyFocused(): void {
    if (this.wbTools?.selectTool.isOn()) {
      this.wbContainer.focused.copy();
    }
  }
  public cutFocused(): void {
    if (this.wbTools?.selectTool.isOn()) {
      this.wbContainer.focused.cut();
    }
  }
  public paste(): void {
    if (this.wbTools?.selectTool.isOn()) {
      this.wbContainer.focused.paste();
    }
  }
  public selectAll(): void {
    this.wbContainer?.focusAll();
  }
  public deleteSelected(): void {
    if (this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.remove();
    }
  }

  public orderTop(): void {
    this.wbContainer?.focused.orderTop();
  }
  public orderBottom(): void {
    this.wbContainer?.focused.orderBottom();
  }

  public recenterRefPoint(call: boolean = true): void {
    this.wbContainer?.focused.recenterRefPoint(call);
  }

  public fill(): void {
    if (this.wbTools.fillTool.isOn()) {
      this.free();
      return;
    }

    this.activateTool('Fill', () => {
      this.wbContainer.blur(undefined, false);
      this.wbTools.fillTool.on();
    });
  }

  public toggleStreaming(): void {
    this.whiteboardService.setStreaming(!this.whiteboard.streaming);
  }
  public toggleSmooth(): void {
    this.whiteboardService.setSmooth(!this.whiteboard.smooth);
  }

  /* callbacks */
  public drawerChangedCallBack(parameters): void {
    if (this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() !== parameters.oldDrawer) {
      this.lastActive.drawer = parameters.oldDrawer;
    }

    this.wbContainer.tools.drawTool.mirroring = parameters.newDrawer instanceof DrawCircle || parameters.newDrawer instanceof DrawEllipse;
  }

  public toolOnCallBack(): void {}
  public toolOffCallBack(parameters): void {
    this.lastActive.tool = parameters.tool;
  }
  public selectToolOnCallBack(): void {}
  public highlighterToolOnCallBack(): void {}
  public pointerToolOnCallBack(): void {
    this.whiteboardService.sendPointerToolOn();
    this.whiteboardService.incrementStaffPointerCount();
  }
  public pointerToolOffCallBack(): void {
    this.whiteboardService.sendPointerToolOff();
    this.whiteboardService.decrementStaffPointerCount();
  }
  public editNodeToolOnCallBack(): void {}
  public editNodeToolOffCallBack(): void {}
  public editTableToolOnCallBack(): void {}
  public editTableToolOffCallBack(): void {}
  public ellipseToolOnCallBack(): void {}
  public circleToolOnCallBack(): void {}
  public rectangleToolOnCallBack(): void {}
  public isoscelesTriangleToolOnCallBack(): void {}
  public rightTriangleToolOnCallBack(): void {}
  public lineToolOnCallBack(): void {}
  public polylineToolOnCallBack(): void {}
  public fillToolOnCallBack(): void {}
  public fillToolOffCallBack(): void {
    this.wbTools.fillTool.fillColor = 'none';
    this.setColorPickerValue('cpFillToolColor', '#00000000');
  }
  public polygonToolOnCallBack(): void {}
  public freehandToolOnCallBack(): void {}
  public textToolOnCallBack(): void {}
  public tableToolOnCallBack(): void {}
  public gridOnCallBack(): void {}
  public gridOffCallBack(): void {}
  public snapOnCallBack(): void {}
  public snapOffCallBack(): void {}

  public focusChangedCallBack(): void {
    this.whiteboardService.sendElementsFocused({elements: this.wbContainer.focused.children});
    this.recenterRefPoint(true);

    let style: Style = this.wbStyle;
    if (this.wbContainer.focused.children.size === 1) {
      const [firstElement] = this.wbContainer.focused.children;
      if (firstElement instanceof GroupView) {
        return;
      } else {
        style = firstElement.style;
      }
    }

    this.setColorPickerValue('cpStroke', style.strokeColor);
    this.setColorPickerValue('cpFill', style.fillColor);
    this.setColorPickerValue('cpFillToolColor', this.wbTools.fillTool.fillColor);
    this.setColorPickerValue('cpFont', style.fontColor);
    this.setColorPickerValue('cpFontBg', style.backgroundColor);
  }
  public clickedOnContainerCallBack(): void {
    if (
      this.wbTools.editNodeTool.isOn() ||
      this.wbTools.editTableTool.isOn() ||
      (
        /* There is no edit text tool. Text editing functionality is written in text box drawer. */
        /* Text drawer is in editing mode, if it is turned on, and isInEditMode is true */

        /* Text box tool is on */
        this.wbTools.drawTool.isOn() && this.wbTools.drawTool.getDrawer() instanceof DrawTextBox &&
        /* Text box tool is in edit mode */
        (this.wbTools.drawTool.getDrawer() as DrawTextBox).isInEditMode
      )
    ) {
      this.free();
    }
  }
  public snapSideChangeCallBack(parameters): void {
    this.whiteboardService.sendSnapSideChange(parameters);
  }
  public elementsDeletedCallBack(parameters): void {
    /* if one of deleted elements is last posted problem header, then reset last posted problem header */
    for (const element of parameters.elements) {
      if (element instanceof ForeignObjectView && !(element instanceof TextBoxView)) {
        /**
         <foreignObject>
            <div> // content
               <div id="<PROBLEM_ORIGINAL_ID>"> // content's first child
                  <img ...
               </div>
            </div>
          </foreignObject>
         */
        const problemElement: HTMLElement = element.content?.firstChild as HTMLElement;

        if (problemElement?.id === this.whiteboard.lastPostedProblemId + '') {
          this.store.dispatch(new WhiteboardAction.SetLastPostedProblemId(null));
          break;
        }
      }
    }
    this.whiteboardService.sendElementsDeleted(parameters);
  }

  public shareScreen(): void {
    this.wrtcProviderService.shareScreen();
  }

  public drawMouseDownCallBack(parameters): void {
    this.whiteboardService.sendDrawMouseDown(parameters);
  }
  public drawMouseMoveCallBack(parameters): void {
    this.whiteboardService.sendDrawMouseMove(parameters);
  }
  public drawMouseUpCallBack(parameters): void {
    this.whiteboardService.sendDrawMouseUp(parameters);
  }
  public elementsCreatedCallBack(parameters): void {
    const elementView: ElementView = parameters.element as ElementView;

    if (this.whiteboard.streaming || elementView.type !== ElementType.TEXT_BOX) {
      this.whiteboardService.sendElementsCreated(parameters);
    }

    if (
      elementView instanceof TableView ||
      elementView instanceof ImageView ||
      elementView instanceof NumberLineView2 ||
      elementView instanceof CoordinatePlaneView2
    ) {
      elementView.erasable = false;
    }
  }
  public enclosedAreaFilledCallBack(parameters): void {
    this.whiteboardService.sendElementsCreated(parameters);
    this.free();
  }
  public stopClickDrawingCallBack(): void {
    this.whiteboardService.sendStopClickDrawing();
  }
  public endDrawingCallBack(parameters): void {
    if (!(parameters.drawer instanceof DrawTextBox || parameters.drawer instanceof FreeDrawer) && this.turnOnDefaultTool) {
      this.free();
    }
  }

  public perfectModeOnCallBack(): void {
    this.whiteboardService.sendPerfectModeOn();
  }
  public perfectModeOffCallBack(): void {
    this.whiteboardService.sendPerfectModeOff();
  }

  public dragMouseDownCallBack(parameters): void {
    this.whiteboardService.sendDragMouseDown(parameters);
  }
  public dragMouseMoveCallBack(parameters): void {
    this.whiteboardService.sendDragMouseMove(parameters);
  }
  public dragMouseUpCallBack(parameters): void {
    this.whiteboardService.sendDragMouseUp(parameters);
  }
  public elementsDraggedCallBack(parameters): void {
    this.whiteboardService.sendElementsDragged(parameters);
  }

  public rotateMouseDownCallBack(parameters): void {
    this.whiteboardService.sendRotatePointMouseDown(parameters);
  }
  public rotateMouseMoveCallBack(parameters): void {
    this.whiteboardService.sendRotatePointMouseMove(parameters);
  }
  public rotateMouseUpCallBack(parameters): void {
    this.whiteboardService.sendRotatePointMouseUp(parameters);
  }
  public elementsRotatedCallBack(parameters): void {
    this.whiteboardService.sendElementsRotated(parameters);
  }

  public resizeMouseDownCallBack(parameters): void {
    this.whiteboardService.sendResizeMouseDown(parameters);
  }
  public resizeMouseMoveCallBack(parameters): void {
    this.whiteboardService.sendResizeMouseMove(parameters);
  }
  public resizeMouseUpCallBack(parameters): void {
    this.whiteboardService.sendResizeMouseUp(parameters);
  }
  public elementsResizedCallBack(parameters): void {
    this.whiteboardService.sendElementsResized(parameters);
  }

  public textTypingCallBack(parameters): void {
    this.whiteboardService.sendTextTyping(parameters);
  }
  public textTypingCommitCallBack(parameters): void {
    if (parameters.oldText === '') {
      this.whiteboardService.sendElementsCreated({
        element: parameters.element
      });
    } else {
      this.whiteboardService.sendTextTypingCommit(parameters);
    }
  }

  public numberLineEditedCallBack(parameters): void {
    this.whiteboardService.sendNumberLineEdit(parameters);
  }
  public coordinatePlaneEditedCallBack(parameters): void {
    this.whiteboardService.sendCoordinatePlaneEdit(parameters);
  }
  public assetEditCallBack(parameters): void {
    this.whiteboardService.sendAssetEdit(parameters);
  }
  public assetEditCommitCallBack(parameters): void {
    this.whiteboardService.sendAssetEditCommit(parameters);
    this.free();
  }

  public refPointChangedCallBack(parameters): void {
    this.whiteboardService.sendRefPointChanged(parameters);
  }
  public recenterRefPointCallBack(parameters): void {
    this.whiteboardService.sendRecenterRefPoint(parameters);
  }

  public nodeEditMouseClickCallBack(parameters): void {
    this.whiteboardService.sendNodeEditMouseClick(parameters);
  }
  public nodeEditMouseDownCallBack(parameters): void {
    this.whiteboardService.sendNodeEditMouseDown(parameters);
  }
  public nodeEditMouseMoveCallBack(parameters): void {
    this.whiteboardService.sendNodeEditMouseMove(parameters);
  }
  public nodeEditMouseUpCallBack(parameters): void {
    this.whiteboardService.sendNodeEditMouseUp(parameters);
  }
  public nodeEditedCallBack(parameters): void {
    this.whiteboardService.sendNodeEdited(parameters);
  }

  public tableEditMouseDownCallBack(parameters): void {
    this.whiteboardService.sendTableEditMouseDown(parameters);
  }
  public tableEditMouseMoveCallBack(parameters): void {
    this.whiteboardService.sendTableEditMouseMove(parameters);
  }
  public tableEditMouseUpCallBack(parameters): void {
    this.whiteboardService.sendTableEditMouseUp(parameters);
  }
  public tableEditedCallBack(parameters): void {
    this.whiteboardService.sendTableEdited(parameters);
  }

  public tableRecreatedCallBack(parameters): void {
    this.whiteboardService.sendTableRecreated(parameters);
  }
  public setTableCallBack(parameters): void {
    this.whiteboardService.sendSetTable(parameters);
  }
  public addTableRowCallBack(parameters): void {
    this.whiteboardService.sendAddTableRow(parameters);
  }
  public addTableColCallBack(parameters): void {
    this.whiteboardService.sendAddTableCol(parameters);
  }
  public removeTableRowCallBack(parameters): void {
    this.whiteboardService.sendRemoveTableRow(parameters);
  }
  public removeTableColCallBack(parameters): void {
    this.whiteboardService.sendRemoveTableCol(parameters);
  }

  public highlightMouseDownCallBack(parameters): void {
    this.whiteboardService.sendHighlightMouseDown(parameters);
  }
  public highlightMouseMoveCallBack(parameters): void {
    this.whiteboardService.sendHighlightMouseMove(parameters);
  }
  public highlightMouseUpCallBack(parameters): void {
    this.whiteboardService.sendHighlightMouseUp(parameters);
  }
  public highlightedCallBack(parameters): void {
    this.whiteboardService.sendHighlighted(parameters);
  }

  public pointerMoveCallBack(parameters): void {
    this.whiteboardService.sendPointerMouseMove(parameters);
  }

  public toTopCallBack(parameters): void {
    this.whiteboardService.sendToTop(parameters);
  }
  public toBottomCallBack(parameters): void {
    this.whiteboardService.sendToBottom(parameters);
  }

  public copyCallBack(parameters): void {
    this.whiteboardService.sendCopy(parameters);
  }
  public cutCallBack(parameters): void {
    this.whiteboardService.sendCut(parameters);
  }
  public pasteCallBack(parameters): void {
    this.whiteboardService.sendPaste(parameters);
  }

  public groupCallBack(parameters): void {
    this.whiteboardService.sendGroup(parameters);
  }
  public ungroupCallBack(parameters): void {
    this.whiteboardService.sendUngroup(parameters);
  }

  public styleChangeCallBack(): void {
    /* force update ui */
  }
  public elementStyleChangeCallBack(style): void {
    const parameters = {
      style,
      elements: this.wbContainer.focused.children
    };
    this.whiteboardService.sendStyleChange(parameters);
  }

  public commitStyleChange(style): void {
    const parameters = {
      style,
      elements: this.wbContainer.focused.children
    };
    this.whiteboardService.sendStyleChangeCommit(parameters);
  }

  public getWBDashArray(): string {
    const visibleDasharray = [];
    const realDasharray = this.wbStyle.strokeDashArray.split(' ');
    const strokeWidth = parseInt(this.wbStyle.strokeWidth);
    realDasharray.forEach(dash => {
      const dashIntValue = parseInt(dash);
      visibleDasharray.push(dashIntValue / strokeWidth);
    });
    return visibleDasharray.join(' ');
  }
  public setWBDashArray(dasharray: string): void {
    const visibleDasharray = [];
    const realDasharray = dasharray.split(' ');
    const strokeWidth = parseInt(this.wbStyle.strokeWidth);
    realDasharray.forEach(dash => {
      const dashIntValue = parseInt(dash);
      visibleDasharray.push(dashIntValue * strokeWidth);
    });
    this.wbStyle.strokeDashArray = visibleDasharray.join(' ');
    this.isDialogOpen.dashStyles = false;
    this.commitStyleChange({strokeDashArray: this.wbStyle.strokeDashArray});
  }
  public setWBStrokeColor(color: string): void {
    this.wbStyle.strokeColor = color;
    this.commitStyleChange({strokeColor: color});
  }
  public setWBFillColor(color: string): void {
    this.wbStyle.fillColor = color;
    this.commitStyleChange({fillColor: color});
  }
  public setWBFillToolColor(color: string): void {
    this.wbTools.fillTool.fillColor = color;
    if (!this.wbTools.fillTool.isOn() && !this.isTransparent(color)) {
      this.fill();
    }
  }
  public setColorPickerValue(colorPickerId: string, color: string): void {
    if (!this.colorPickers) {
      return;
    }

    const cpList = this.colorPickers.filter((colorPicker: CustomColorPickerComponent) => colorPicker.id === colorPickerId);
    if (cpList.length !== 0) {
      cpList[0].color = color;
    }
  }
  public getWBStyleStrokeWidth(): string {
    return this.wbStyle.strokeWidth;
  }
  public setWBStyleStrokeWidth(strokeWidth: number): void {
    this.wbStyle.strokeWidth = strokeWidth.toString();
    this.commitStyleChange({strokeWidth: this.wbStyle.strokeWidth/*, strokeDashArray: this.wbStyle.strokeDashArray*/});
    this.isDialogOpen.strokesMore = false;
  }
  public toggleStrokeDropdown($event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.isDialogOpen.strokesMore = !this.isDialogOpen.strokesMore;
  }

  public getStrokeStyle(): string {
    if (this.isTransparent(this.wbStyle.strokeColor)) {
      return 'none';
    }

    const index: number = this.strokesList
        .concat(this.strokesMoreList)
        .findIndex((stroke: number): boolean => stroke + '' === this.wbStyle.strokeWidth)
        + 1;

    return (index * Math.pow(index, 1/3)) + 'px solid ' + this.wbStyle.strokeColor;
  }
  public isTransparent(color: string): boolean {
    return color === 'transparent' || color === 'none' || color === '#00000000';
  }

  public colorPickerVisibilityChange(event: { id: string; oldValue: boolean; newValue: boolean }): void {
    // Close other colorPickers if open
    this.colorPickers.forEach((cp) => {
      if (cp.id !== event.id) {
        cp.closeColorPicker();
      }
    });
  }

  public toggleDashStylesDropdown($event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.isDialogOpen.dashStyles = !this.isDialogOpen.dashStyles;
  }
  public toggleFontSizeDropdown($event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.isDialogOpen.fontSizeMore = !this.isDialogOpen.fontSizeMore;
  }
  public setFontSize(size: number): void {
    this.wbStyle.fontSize = this.mkStr(size);
    this.commitStyleChange({fontSizeMore: size});
  }
  public setCustomFontSize(size: number, $event): void {
    this.wbStyle.fontSize = this.mkStr(size);
    this.commitStyleChange({fontSizeMore: size});
    $event.stopPropagation();
    this.isDialogOpen.fontSizeMore = false;
  }

  public setFontColor(color: string): void {
    this.wbStyle.fontColor = color;
    this.commitStyleChange({fontColor: color});
  }
  public setFontBgColor(color: string): void {
    this.wbStyle.backgroundColor = color;
    this.commitStyleChange({backgroundColor: color});
  }

  public areSameColors(colorStr1: string, colorStr2: string): boolean {
    const normalize = (c: string) => {
      return c.length < 9 ? c + 'ff' : c;
    };
    return normalize(colorStr1.toLowerCase()) === normalize(colorStr2.toLowerCase());
  }

  public share(): void {
    this.coreService.shareWhiteboards();
  }
  public unshare(): void {
    this.coreService.unshareWhiteboards();
  }

  public sendUnloadAssignment(): void {
    this.assignmentService.sendUnload().subscribe();
    this.webPortalMessagingService.sendUnloadAssignment();
  }
  public reloadAssignment(): void {
    this.store.dispatch(new AssignmentAction.RefreshAssignment());
    this.jsonLogService.writeJsonLogs(JsonLogAction.ASMT_REFRESH,{}).subscribe();
  }

  public switchViewPortTab(viewportTab: ViewportTab): void {
    this.store.dispatch(new ClassroomAction.SetBoardTab(viewportTab));
    this.store.dispatch(new LocalUserAction.SetCurrentTab(viewportTab));
    this.applicationRef.tick(); /* force update ui to make whiteboard wrapper visible */
    this.whiteboardService.updateGrid();
    this.whiteboardService.updateScroll();
  }

  public toggleWBTabSwitcherForAll(): void {
    const enable = this.assignment.allowedSwitchingUsers === null;
    if (enable) {
      this.store.dispatch(new AssignmentAction.AllowSwitchAccessForAll());
      this.assignmentService.sendAllowSwitchForAll().subscribe();
    } else {
      this.store.dispatch(new AssignmentAction.DisallowSwitchAccessForAll());
      this.assignmentService.sendDisallowSwitchForAll().subscribe();
    }
  }

  public extendWhiteboard(): void {
    this.whiteboardService.extend();
  }

  public isExtendNotApplicable(): boolean {
    return this.whiteboardService.cannotBeExtended();
  }

  public refreshWhiteboard(): void {
    this.whiteboardService.refresh();
  }

  public openCurriculum(): void {
    this.curriculumService.open();
  }
  public openResetWhiteboardDialog(): void {
    if (window.confirm('Are you sure you want to reset the Whiteboard?')) {
      /* @TODO temporarily disabled WB snapshot saving future */
      this.resetWhiteboard();
      // if (this.classroom.privateTalkInfo.isPrivateWhiteboardOn && this.classroom.showPrivateWhiteboard || this.whiteboardService instanceof PrivateWhiteboardService) {
      //   this.resetWhiteboard();
      // } else {
      //   /* save the main whiteboard snapshot then reset, even if snapshot saving can't be done */
      //   this.coreService.exportAndSaveWhiteboardSnapshot()
      //     .then()
      //     .catch()
      //     .finally((): void => {
      //       this.resetWhiteboard();
      //     });
      // }
    }
  }
  public toggleRestrictedMode(): void {
    this.isInRestrictedMode = !this.isInRestrictedMode;

    if (this.isInRestrictedMode) {
      this.wbActiveTool.off();
    } else {
      this.free();
    }
  }

  private resetWhiteboard(): void {
    this.whiteboardService.reset();
    this.coreService.unshareWhiteboards();

    this.colorPickers.forEach((colorPicker): void => {
      colorPicker.color = this.colorForPicker(colorPicker.id);
    });
  }

  private colorForPicker(colorPickerId: string): string {
    switch (colorPickerId) {
      case 'cpStroke': {
        return this.wbStyle.strokeColor;
      }
      case 'cpFill': {
        return this.wbStyle.fillColor;
      }
      case 'cpFillToolColor': {
        return this.wbFillToolColor;
      }
      case 'cpFont': {
        return this.wbStyle.fontColor;
      }
      case 'cpFontBg': {
        return this.wbStyle.backgroundColor;
      }
      default: {
        throw Error(`No color found for picker with id ${colorPickerId}`);
      }
    }
  }

  public activateTool(toolName: string, callBack: () => void): void {
    if (this.whiteboard) {
      this.turnOnDefaultTool = false;
      callBack();
      this.turnOnDefaultTool = true;
      this.log(toolName);
    }
  }

  public mkPx(value: number): string {
      return value + 'px';
  }
  public mkStr(value: number): string {
    return value + '';
  }

  /* - PRIVATE METHODS - */
  private log(message): void {
    this.jsonLogService.writeJsonLogs(JsonLogAction.WB_TOOL_ACTIVATE, {message}).subscribe();
  }

  private listenStoreChanges(): void {
    this.store.select(LocalUserState.getLocalUser).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });
  }

  private showErrorDialog(error: WebSocket.MessageSendStatus, messages: {[key: string]: string}): void {
    switch (error) {
      case WebSocket.MessageSendStatus.LIMIT_EXCEEDED: {
        const dialog: NgbModalRef = this.dialogService.open(ErrorDialogComponent);
        dialog.componentInstance.messageHeader = 'Action failed.';

        let message: string = messages ? messages[WebSocket.MessageSendStatus.LIMIT_EXCEEDED] : undefined;
        if (!message) {
          message = 'This action could not be transferred to other class participants. Please try again or contact support.';
        }

        dialog.componentInstance.messageContent = message;
        break;
      }
    }
  }

  private init(): void {
    this.wbContainer.addCallBack(SVGEvent.DRAWER_CHANGED, this.drawerChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TOOL_ON, this.toolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TOOL_OFF, this.toolOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.SELECT_TOOl_ON, this.selectToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.HIGHLIGHT_TOOl_ON, this.highlighterToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.POINTER_TOOl_ON, this.pointerToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.POINTER_TOOl_OFF, this.pointerToolOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.EDIT_NODE_TOOl_ON, this.editNodeToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.EDIT_NODE_TOOl_OFF, this.editNodeToolOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.EDIT_NODE_TOOl_OFF, this.editNodeToolOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.EDIT_TABLE_TOOL_ON, this.editTableToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.EDIT_TABLE_TOOL_OFF, this.editTableToolOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELLIPSE_TOOL_ON, this.ellipseToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.CIRCLE_TOOL_ON, this.circleToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.RECTANGLE_TOOL_ON, this.rectangleToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ISOSCELES_TRIANGLE_TOOL_ON, this.isoscelesTriangleToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.RIGHT_TRIANGLE_TOOL_ON, this.rightTriangleToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.LINE_TOOL_ON, this.lineToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.POLYLINE_TOOL_ON, this.polylineToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.POLYGON_TOOL_ON, this.polygonToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.FILL_TOOl_ON, this.fillToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.FILL_TOOl_OFF, this.fillToolOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.FREE_HAND_TOOL_ON, this.freehandToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TEXT_TOOL_ON, this.textToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TABLE_TOOL_ON, this.tableToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.GRID_ON, this.gridOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.GRID_OFF, this.gridOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.SNAP_ON, this.snapOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.SNAP_OFF, this.snapOffCallBack.bind(this));

    /* streaming callbacks */
    this.wbContainer.addCallBack(SVGEvent.DRAW_MOUSE_DOWN, this.drawMouseDownCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAW_MOUSE_MOVE, this.drawMouseMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAW_MOUSE_UP, this.drawMouseUpCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.STOP_CLICK_DRAWING, this.stopClickDrawingCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.END_DRAWING, this.endDrawingCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.DRAG_MOUSE_DOWN, this.dragMouseDownCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAG_MOUSE_MOVE, this.dragMouseMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAG_MOUSE_UP, this.dragMouseUpCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.ROTATE_MOUSE_DOWN, this.rotateMouseDownCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ROTATE_MOUSE_MOVE, this.rotateMouseMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ROTATE_MOUSE_UP, this.rotateMouseUpCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.RESIZE_MOUSE_DOWN, this.resizeMouseDownCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.RESIZE_MOUSE_MOVE, this.resizeMouseMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.RESIZE_MOUSE_UP, this.resizeMouseUpCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.NODE_EDIT_MOUSE_CLICK, this.nodeEditMouseClickCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.NODE_EDIT_MOUSE_DOWN, this.nodeEditMouseDownCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.NODE_EDIT_MOUSE_MOVE, this.nodeEditMouseMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.NODE_EDIT_MOUSE_UP, this.nodeEditMouseUpCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.TABLE_EDIT_MOUSE_DOWN, this.tableEditMouseDownCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TABLE_EDIT_MOUSE_MOVE, this.tableEditMouseMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TABLE_EDIT_MOUSE_UP, this.tableEditMouseUpCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.HIGHLIGHT_MOUSE_DOWN, this.highlightMouseDownCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.HIGHLIGHT_MOUSE_MOVE, this.highlightMouseMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.HIGHLIGHT_MOUSE_UP, this.highlightMouseUpCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.TEXT_TYPING, this.textTypingCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ASSET_EDIT, this.assetEditCallBack.bind(this));
    /* streaming callbacks */

    this.wbContainer.addCallBack(SVGEvent.ENCLOSED_AREA_FILLED, this.enclosedAreaFilledCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENT_CREATED, this.elementsCreatedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_DRAGGED, this.elementsDraggedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_ROTATED, this.elementsRotatedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_RESIZED, this.elementsResizedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.NODE_EDITED, this.nodeEditedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TABLE_EDITED, this.tableEditedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TABLE_RECREATED, this.tableRecreatedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.SET_TABLE, this.setTableCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ADD_TABLE_ROW, this.addTableRowCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ADD_TABLE_COL, this.addTableColCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.REMOVE_TABLE_ROW, this.removeTableRowCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.REMOVE_TABLE_COL, this.removeTableColCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.HIGHLIGHTED, this.highlightedCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_DELETED, this.elementsDeletedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_FOCUSED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_BLURRED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ALL_FOCUSED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ALL_BLURRED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.CLICKED_ON_CONTAINER, this.clickedOnContainerCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.SNAP_SIDE_CHANGE, this.snapSideChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.PERFECT_MODE_ON, this.perfectModeOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.PERFECT_MODE_OFF, this.perfectModeOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TEXT_TYPING_COMMIT, this.textTypingCommitCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.NUMBER_LINE2_EDITED, this.numberLineEditedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.COORDINATE_PLANE2_EDITED, this.coordinatePlaneEditedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ASSET_EDIT_COMMIT, this.assetEditCommitCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.REF_POINT_CHANGED, this.refPointChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.RECENTER_REFERENCE_POINT, this.recenterRefPointCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.POINTER_MOVE, this.pointerMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TO_TOP, this.toTopCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TO_BOTTOM, this.toBottomCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.COPY, this.copyCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.CUT, this.cutCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.PASTE, this.pasteCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.GROUP, this.groupCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.UNGROUP, this.ungroupCallBack.bind(this));

    /* style callbacks */
    this.wbContainer.addCallBack(SVGEvent.STYLE_CHANGE, this.styleChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.STROKE_WIDTH_CHANGE, this.elementStyleChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.STROKE_DASH_ARRAY_CHANGE, this.elementStyleChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.STROKE_COLOR_CHANGE, this.elementStyleChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.FILL_COLOR_CHANGE, this.elementStyleChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.FONT_SIZE_CHANGE, this.elementStyleChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.FONT_COLOR_CHANGE, this.elementStyleChangeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.FONT_BACKGROUND_CHANGE, this.elementStyleChangeCallBack.bind(this));
    /* style callbacks */

    this.activeDrawer.linear = this.wbContainer.drawers.line;
    this.activeDrawer.shape = this.wbContainer.drawers.circle;
    this.activeDrawer.cartesian = this.wbContainer.drawers.coordinatePlane2;

    /* turn off old pointer tool, and initialize new one */
    this.wbTools.pointerTool.off(false);
    this.wbTools.pointerTool = this.whiteboardService.getStaffPointerTool();

    document.addEventListener('mousedown', (mouseEvent: MouseEvent): void => {
      const wbAssignmentContainer: HTMLElement = document.getElementById('whiteboardAssignmentContainer');
      const privateWbAssignmentContainer: HTMLElement = document.getElementById('privateWhiteboardAssignmentContainer');
      const target = mouseEvent.target as HTMLElement;

      if (wbAssignmentContainer.contains(target) || privateWbAssignmentContainer.contains(target)) {
        return;
      }

      this.whiteboard.container.blur();
    }, false);

    /* disable dragging for button images */
    const buttonImages: HTMLCollectionOf<Element> = document.getElementsByClassName('no-drag');
    for (let i = 0; i < buttonImages.length; i++) {
      const imgElem: HTMLElement = buttonImages.item(i) as HTMLElement;
      imgElem.ondragstart = () => {
        return false;
      };
    }

    if (!this.isInRestrictedMode) {
      this.free();
    }
  }

  protected readonly PointType = PointType;
}
